import type { FC, MouseEvent } from 'react';
import React from 'react';
import cn from 'classnames';
import { useNamedContainers } from '@alfa-bank/site-named-container/utils';
import { CORE_INPUT_ENABLE } from '@alfa-bank/site-envconfig';
// eslint-disable-next-line regex/invalid
import { getHandler } from '@alfa-bank/site-pure-input/src/utils';

import { isEnableCoreInput, getInputType } from './helpers';
import type { TInput } from './models/models';

// eslint-disable-next-line complexity
export const Input: FC<TInput> = ({
    CoreInput,
    Hint,
    IconError,
    IconSuccess,
    PureInput,
    Text,
    addHiddenInput,
    block = true,
    caption,
    children,
    className = '',
    clear = false,
    dataTestId = 'input',
    disabled,
    error,
    forwardedRef,
    hideStatusIcon,
    id,
    infoBlock,
    infoText,
    labelView,
    name = '',
    onBlur,
    onCancel,
    onChange,
    onClick,
    onFocus,
    onKeyDown,
    onMouseDown,
    onMouseUp,
    onPaste,
    paddingleft,
    placeholder: label,
    placeholderText,
    size = 'm',
    styles,
    success,
    type,
    valid,
    value = '',
    ...rest
    // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
    /** Флаг переключения на @alfalab/core-components-input
     * После перехода сайта на новый инпут, удалить
     */
    const isCoreInput = isEnableCoreInput(CORE_INPUT_ENABLE.value());

    /** CoreInput поддерживает аддоны, у нас используем слоты
     *  Посмотреть тут https://core-ds.github.io/core-components/master/?path=/story/input--input
     */
    const { bottomAddon, leftAddon, rightAddon } = useNamedContainers(children);

    /** Скрытый input */
    const HiddenInput = addHiddenInput && disabled && (
        <input name={name} readOnly type="hidden" value={value} />
    );

    /** Если есть ошибка то для текста ошибки используем ее
     *  иначе hint будет на месте текста для ошибки
     */
    const hintText = error ? '' : caption;

    /** У коров для ошибки можно передать как строку как и флаг
     *  у нас есть отдельный пропс caption для текста ошибки
     */
    const errorContent = (error && caption && !infoBlock ? caption : '') || error;

    return (
        /** Т.к в CoreInput нет type hidden, скрываем всю разметку */
        <div className={styles.inputWrapper} hidden={isCoreInput && type === 'hidden'}>
            {isCoreInput ? (
                <>
                    <CoreInput
                        block={block}
                        bottomAddons={bottomAddon}
                        className={className}
                        clear={Boolean(errorContent && clear)}
                        dataTestId={dataTestId}
                        disabled={disabled}
                        error={errorContent}
                        hint={hintText}
                        id={id}
                        label={label}
                        labelView={labelView}
                        leftAddons={leftAddon}
                        name={name}
                        onBlur={getHandler(onBlur)}
                        onChange={getHandler(onChange)}
                        onClear={onCancel}
                        onClick={onClick as (event: MouseEvent<HTMLDivElement>) => void}
                        onFocus={getHandler(onFocus)}
                        onKeyDown={getHandler(onKeyDown)}
                        onMouseDown={
                            /** Костыль т.к в CoreInput HTMLDivElement а не HTMLInputElement*/
                            getHandler(onMouseDown) as (event: MouseEvent<HTMLDivElement>) => void
                        }
                        onMouseUp={
                            /** Костыль т.к в CoreInput HTMLDivElement а не HTMLInputElement*/
                            getHandler(onMouseUp) as (event: MouseEvent<HTMLDivElement>) => void
                        }
                        onPaste={getHandler(onPaste)}
                        placeholder={placeholderText}
                        rightAddons={
                            rightAddon || (infoBlock && infoText && <Hint>{infoText}</Hint>)
                        }
                        size={size}
                        /** Если иконка скрыта не показываем ее
                         *  valid оставленно для обратной совместимости
                         */
                        success={Boolean(!hideStatusIcon && (success || valid))}
                        type={getInputType(type)}
                        value={String(value)}
                        {...rest}
                        ref={forwardedRef}
                    />
                    {HiddenInput}
                </>
            ) : (
                <>
                    <label className={styles.label} htmlFor={id}>
                        <PureInput
                            className={className}
                            dataTestId={dataTestId}
                            disabled={disabled}
                            error={error}
                            forwardedRef={forwardedRef}
                            hasFloatingLabel={Boolean(label)}
                            id={id}
                            name={name}
                            onBlur={onBlur}
                            onChange={onChange}
                            onClick={onClick}
                            onFocus={onFocus}
                            onKeyDown={onKeyDown}
                            onMouseDown={onMouseDown}
                            onMouseUp={onMouseUp}
                            onPaste={onPaste}
                            paddingleft={paddingleft}
                            type={type}
                            valid={valid}
                            value={value}
                            {...rest}
                        />

                        {HiddenInput}

                        <Text
                            className={cn(styles.placeholder, {
                                [styles.emptyValue]: value === '',
                            })}
                            dataTestId={`${dataTestId}-placeholder`}
                            kind="secondary"
                            tag="span"
                            view={value === '' ? 'component' : 'primary-small'}
                        >
                            {label}
                        </Text>
                    </label>

                    <div className={styles.status}>
                        {infoBlock && infoText && <Hint>{infoText}</Hint>}
                        {!hideStatusIcon && success && (
                            <IconSuccess
                                className={styles.iconSucessColor}
                                data-test-id={`success-icon-${name}`}
                            />
                        )}
                        {!hideStatusIcon && error && (
                            <span className={styles.cancel} data-test-id="cancel-button">
                                <IconError
                                    className={styles.iconErrorColor}
                                    data-test-id={`error-icon-${name}`}
                                />
                            </span>
                        )}
                    </div>

                    {caption && (
                        <Text
                            className={cn(styles.external, styles.externalInner, styles.caption, {
                                [styles.captionError]: error && !infoBlock,
                            })}
                            dataTestId={
                                error && !infoBlock ? `captionError-${name}` : `caption-${name}`
                            }
                            kind="caption"
                            view="primary-small"
                        >
                            {caption}
                        </Text>
                    )}
                </>
            )}
        </div>
    );
};
